import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AllInclusiveService } from '@app/core/services/all-inclusive.service';
import { FirmCollaboratorActions } from '@app/core/state/firm-collaborators/firm-collaborators.actions';
import {
  CollaboratorTextTranslations,
  FirmCollaboratorSelectors,
} from '@app/core/state/firm-collaborators/firm-collaborators.selectors';
import { openTranslatableDialog } from '@app/shared/misc/openDialog';
import { TranslocoModule } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { map, mergeMap, Observable, of } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { SafeHtmlPipe } from 'src/app/shared/pipes/safe-html.pipe';
import { FirmCollaboratorDialogComponent } from './firm-collaborator-dialog.component';
import { BlFrontendButtonComponent } from 'src/app/core/wrappers/bl-frontend-button.component';

@Component({
  selector: 'app-firm-collaborators',
  template: `<ng-container *transloco="let t; prefix: scope">
    <h1 class="!text-base font-semibold">{{ t('cooperationAgency') }}</h1>
    <p class="mb-4" [innerHTML]="t('cooperationAgencyMessage') | safeHtml"></p>
    <div class="flex flex-col">
      <div class="flex items-center">
        <span class="font-semibold mr-2">{{ t('connectedAgencies') }}</span>
      </div>
      <div class="flex flex-nowrap">
        <span class="p-2 border border-bl-grey-500 bg-bl-grey-100 min-w-72">{{ firmCollaboratorsText$ | async }}</span>
        <bl-frontend-button
          variant="tonal"
          [trailingIcon]="{ name: 'edit', size: 'lg' }"
          class="ml-2"
          (click)="editFirmCollaborators()"
          [disabled]="(canEditCollaborators$ | async) === false"
          [title]="t('editConnectedAgencies')">
        </bl-frontend-button>
      </div></div
  ></ng-container>`,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslocoModule, SafeHtmlPipe, BlFrontendButtonComponent],
  providers: [getTranslationProvider('FirmCollaboratorComponent')],
})
export class FirmCollaboratorComponent extends TranslatableComponent<CollaboratorTextTranslations> implements OnInit {
  @Input() clientId: number;
  @Input() cloudApiKey: string;

  firmCollaboratorsText$: Observable<string>;
  canEditCollaborators$: Observable<boolean>;

  get hasCloudApiKey(): boolean {
    return Boolean(this.cloudApiKey);
  }

  constructor(
    private dialogService: DialogService,
    private allInclusiveService: AllInclusiveService,
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.firmCollaboratorsText$ = this.getFirmCollaboratorsTextObservable();
    this.canEditCollaborators$ = this.getCanEditCollaboratorsObservable();
    this.loadFirmCollaborators();
  }

  editFirmCollaborators() {
    openTranslatableDialog({
      deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
      componentType: FirmCollaboratorDialogComponent,
      data: { clientId: this.clientId },
    });
  }

  private getFirmCollaboratorsTextObservable(): Observable<string> {
    if (!this.allInclusiveService.isLoggedInWithBLTAService) {
      return this.translationsLoaded$.pipe(map((translation) => translation?.onlyCoworkersOfAFirm));
    }

    if (!this.hasCloudApiKey) {
      return this.translationsLoaded$.pipe(map((translation) => translation?.cloudDatabaseKeyMustExists));
    }

    return this.translationsLoaded$.pipe(
      mergeMap((translations) =>
        this.store.select(
          FirmCollaboratorSelectors.selectCollaboratorsText(
            this.clientId,
            translations as CollaboratorTextTranslations,
          ),
        ),
      ),
    );
  }

  private getCanEditCollaboratorsObservable(): Observable<boolean> {
    if (!this.allInclusiveService.isLoggedInWithBLTAService) {
      return of(false);
    }
    return this.store.select(FirmCollaboratorSelectors.canEditCollaboratorsForClientId(this.clientId));
  }

  private loadFirmCollaborators() {
    if (!this.hasCloudApiKey || !this.allInclusiveService.isLoggedInWithBLTAService) {
      return;
    }
    this.store.dispatch(FirmCollaboratorActions.loadFirmCollaboratorsForClientIfMissing({ clientId: this.clientId }));
  }
}
